var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import tracker from 'tracker';
import get from 'lodash/get';
import shallowEqual from 'utils/shallowEqual';
import fdpHelper from 'utils/FDPHelper';
export var ImpressionEvent;
(function (ImpressionEvent) {
    ImpressionEvent["DCI"] = "DCI";
    ImpressionEvent["DWI"] = "DWI";
    ImpressionEvent["SOI"] = "SOI";
})(ImpressionEvent || (ImpressionEvent = {}));
export var ClickEvent;
(function (ClickEvent) {
    ClickEvent["DWC"] = "DWC";
    ClickEvent["DCC"] = "DCC";
})(ClickEvent || (ClickEvent = {}));
export var CustomEvent;
(function (CustomEvent) {
    CustomEvent["ATCC"] = "ATCC";
})(CustomEvent || (CustomEvent = {}));
const isTrackingObjectARecord = (tracking) => {
    const isObject = typeof tracking === 'object' && tracking !== null;
    return isObject && !Array.isArray(tracking);
};
/**
 * This component tries to abstarct out the tracking requirements
 * @param Component
 * @param impressionEvent
 */
export default function TrackableComponent(Component, impressionEvent, mapPropsToTracking) {
    var _a;
    return _a = class TrackingComponent extends React.Component {
            constructor(props) {
                super(props);
                this.widget = null;
                this.trackImpressionFactory = (en) => (tracking, ref, intersectionObserverRootMargin) => {
                    if (!tracking || !isTrackingObjectARecord(tracking))
                        return;
                    const { wid, parentImpressionId, position: actualPosition } = this.props;
                    const { impressionId, contentType, position, suggestionId, requestId, responseTimeInMS } = tracking;
                    const trackingInfo = {
                        en,
                        piid: parentImpressionId,
                        iid: impressionId,
                        wk: wid,
                        ct: contentType,
                        p: position || actualPosition,
                        sid: suggestionId,
                        rid: requestId
                    };
                    if (typeof responseTimeInMS !== 'undefined') {
                        trackingInfo.pl = responseTimeInMS;
                    }
                    if (ref && impressionId) {
                        tracker.track(ref, trackingInfo, undefined, intersectionObserverRootMargin);
                    }
                };
                this.trackEvent = (tracking, force = false) => {
                    const { parentImpressionId } = this.props;
                    if (!tracking)
                        return;
                    const { iid, en } = tracking, rest = __rest(tracking, ["iid", "en"]);
                    if (en && iid) {
                        fdpHelper.trackEvent(Object.assign({ en,
                            iid, piid: parentImpressionId }, rest), force);
                    }
                };
                this.trackClickFactory = (en) => (tracking) => {
                    if (!tracking || !isTrackingObjectARecord(tracking))
                        return;
                    const { impressionId, contentType, position } = tracking;
                    this.trackEvent({
                        en,
                        iid: impressionId,
                        ct: contentType,
                        p: position
                    }, true);
                };
                this.untrackImpression = (ref) => {
                    if (ref) {
                        tracker.untrack(ref);
                    }
                };
                this.innerRef = (c) => {
                    this.widget = c;
                    if (__DEBUG_ENV__) {
                        const { parentImpressionId } = this.props;
                        parentImpressionId &&
                            c &&
                            !c.hasAttribute('data-fdp-tracking-test') &&
                            c.setAttribute('data-fdp-tracking-test', parentImpressionId);
                    }
                };
                this.trackContentImpression = this.trackImpressionFactory(ImpressionEvent.DCI);
                this.trackWidgetImpression = this.trackImpressionFactory(ImpressionEvent.DWI);
                this.trackContentClick = this.trackClickFactory(ClickEvent.DCC);
                this.trackWidgetClick = this.trackClickFactory(ClickEvent.DWC);
                this.trackAutosuggestItemImpression = this.trackImpressionFactory(ImpressionEvent.SOI);
            }
            componentDidMount() {
                this.startTracking(this.props);
            }
            componentDidUpdate(prevProps) {
                /**
                 * If the component gets updated with new props, the tracker lib will still trigger events
                 * using old props. Hence, we need to update the tracking props
                 */
                const prevTracking = mapPropsToTracking ? mapPropsToTracking(prevProps) : get(prevProps, 'widget.tracking', {});
                const tracking = mapPropsToTracking ? mapPropsToTracking(this.props) : get(this.props, 'widget.tracking', {});
                if (!shallowEqual(prevTracking, tracking)) {
                    this.untrackImpression(this.widget);
                    this.startTracking(this.props);
                }
            }
            componentWillUnmount() {
                const tracking = mapPropsToTracking ? mapPropsToTracking(this.props) : get(this.props, 'widget.tracking');
                tracking && this.untrackImpression(this.widget);
            }
            startTracking(props) {
                const tracking = mapPropsToTracking
                    ? mapPropsToTracking(props)
                    : get(props, 'widget.tracking');
                if (!tracking)
                    return;
                switch (impressionEvent) {
                    case ImpressionEvent.DWI:
                        this.trackWidgetImpression(tracking, this.widget);
                        return;
                    case ImpressionEvent.DCI:
                        this.trackContentImpression(tracking, this.widget);
                        return;
                    case ImpressionEvent.SOI:
                        this.trackAutosuggestItemImpression(tracking, this.widget);
                        return;
                    default:
                        return;
                }
            }
            render() {
                return (<Component {...this.props} innerRef={this.innerRef} trackAutosuggestItemImpression={this.trackAutosuggestItemImpression} trackContentClick={this.trackContentClick} trackContentImpression={this.trackContentImpression} trackWidgetImpression={this.trackWidgetImpression} trackWidgetClick={this.trackWidgetClick} untrackImpression={this.untrackImpression} trackEvent={this.trackEvent}/>);
            }
        },
        _a.displayName = `TrackableComponent(${Component.displayName || Component.name || 'TrackableComponent'})`,
        _a;
}
